import { ReactNode, HTMLAttributes } from 'react';
import clsx from 'clsx';

export type BadgeVariant = 'success' | 'warning' | 'danger' | 'info' | 'neutral' | 'primary' | 'purple';
export type BadgeSize = 'sm' | 'md' | 'lg';

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant;
  size?: BadgeSize;
  dot?: boolean;
  icon?: ReactNode;
}

const variantStyles: Record<BadgeVariant, string> = {
  success: 'bg-accent-100 text-accent-700 dark:bg-accent-700/20 dark:text-accent-300',
  warning: 'bg-amber-100 text-amber-800 dark:bg-amber-700/20 dark:text-amber-300',
  danger: 'bg-red-100 text-red-800 dark:bg-red-700/20 dark:text-red-300',
  info: 'bg-primary-100 text-primary-700 dark:bg-primary-700/20 dark:text-primary-300',
  neutral: 'bg-gray-100 text-gray-800 dark:bg-gray-700/30 dark:text-gray-300',
  primary: 'bg-primary-100 text-primary-700 dark:bg-primary-700/20 dark:text-primary-300',
  purple: 'bg-violet-100 text-violet-600 dark:bg-violet-500/20 dark:text-violet-200',
};

const sizeStyles: Record<BadgeSize, string> = {
  sm: 'px-2 py-0.5 text-xs',
  md: 'px-2.5 py-1 text-xs',
  lg: 'px-3 py-1.5 text-sm',
};

const dotStyles: Record<BadgeVariant, string> = {
  success: 'bg-accent-500',
  warning: 'bg-amber-500',
  danger: 'bg-red-500',
  info: 'bg-primary-500',
  neutral: 'bg-gray-500',
  primary: 'bg-primary-500',
  purple: 'bg-violet-500',
};

export const Badge = ({
  variant = 'neutral',
  size = 'md',
  dot = false,
  icon,
  children,
  className,
  ...props
}: BadgeProps) => {
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1 font-medium rounded-full whitespace-nowrap',
        variantStyles[variant],
        sizeStyles[size],
        className
      )}
      {...props}
    >
      {dot && (
        <span
          className={clsx(
            'w-1.5 h-1.5 rounded-full',
            dotStyles[variant]
          )}
        />
      )}
      {icon && <span className="flex-shrink-0">{icon}</span>}
      {children}
    </span>
  );
};
